import { useRef, useEffect } from 'react';

export const GRACE_CHARACTERS = 5;

type InputElement = HTMLInputElement | HTMLTextAreaElement;

interface UseCharacterLimitOptions<T extends InputElement> {
  maxLength: number;
  onChange?: (e: React.ChangeEvent<T>) => void;
}

export function useCharacterLimit<T extends InputElement>({
  maxLength,
  onChange,
}: UseCharacterLimitOptions<T>) {
  const elementRef = useRef<T | null>(null);
  const trimTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleChange = (e: React.ChangeEvent<T>) => {
    const inputValue = e.target.value;
    const isOverMaxLength = inputValue.length >= maxLength;
    const isWithinGracePeriod = inputValue.length <= maxLength + GRACE_CHARACTERS;

    onChange && onChange(e);

    if (isOverMaxLength && isWithinGracePeriod) {
      if (trimTimeoutRef.current) {
        clearTimeout(trimTimeoutRef.current);
      }
      trimTimeoutRef.current = setTimeout(() => {
        if (elementRef.current) {
          const trimmedValue = inputValue.substring(0, maxLength - 1);
          elementRef.current.value = trimmedValue;
          const event = new Event('input', { bubbles: true });
          elementRef.current.dispatchEvent(event);
          if (onChange && elementRef.current) {
            const simpleEvent = { target: elementRef.current } as React.ChangeEvent<T>;
            onChange(simpleEvent);
          }
        }
      }, 1000);
    }

    if (inputValue.length > maxLength + GRACE_CHARACTERS) {
      e.target.value = inputValue.substring(0, maxLength + GRACE_CHARACTERS);
    }
  };

  const isAtOrOverMaxLength = (value?: string | number): boolean => {
    if (value === undefined || value === null) return false;
    return String(value).length >= maxLength;
  };

  useEffect(() => {
    return () => {
      if (trimTimeoutRef.current) {
        clearTimeout(trimTimeoutRef.current);
      }
    };
  }, []);

  return {
    elementRef,
    handleChange,
    isAtOrOverMaxLength,
    effectiveMaxLength: maxLength + GRACE_CHARACTERS,
  };
}